/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Tooltip, Tag, Empty, Spin, Statistic, Row, Col } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { EVENT_TYPES, EVENT_COLORS } from './mockData';
import './DailyActivityDialogDemo.less';

const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  let result = '';
  if (hours > 0) result += `${hours}小时`;
  if (minutes > 0) result += `${minutes}分钟`;
  if (secs > 0) result += `${secs}秒`;
  return result || '-';
};

const reviewStatusTextMap = {
  PASS: '审核通过',
  PENDING: '待审核',
  NOT_APPROVED: '审核不通过',
  REVOKED: '已撤销',
};

const reviewStatusColorMap = {
  PASS: 'green',
  PENDING: 'orange',
  NOT_APPROVED: 'red',
  REVOKED: 'default',
};

// ---------------------------------------------------------------------------
// 甘特图辅助函数(参考 StoreDetailDialog)
// ---------------------------------------------------------------------------

// 检测两个事件是否有时间重叠
const hasEventOverlap = (event1, event2) => {
  if (!event1 || !event2) return false;
  const event1Start = event1.timestamp;
  const event1End = event1.timestamp + (event1.durationSeconds || 0) * 1000;
  const event2Start = event2.timestamp;
  const event2End = event2.timestamp + (event2.durationSeconds || 0) * 1000;

  return event1Start < event2End && event2Start < event1End;
};

// 获取重叠事件列表
const getTooltipEvents = (event, allEvents) => {
  if (!event || !Array.isArray(allEvents) || allEvents.length === 0) return [];
  const overlappedEvents = allEvents.filter((e) => hasEventOverlap(event, e));
  if (!overlappedEvents.length) return [];
  return [event, ...overlappedEvents]
    .sort((a, b) => a.timestamp - b.timestamp)
    .map((e, i) => ({
      ...e,
      tooltipKey: `${e.eventId || 'event'}-${e.timestamp}-${i}`,
    }));
};

// 为轨道上的 blocks 分配 tooltip 事件(聚合重叠事件)
const assignTrackTooltipEvents = (trackBlocks, totalDuration) => {
  if (!Array.isArray(trackBlocks) || trackBlocks.length === 0) return [];

  const sortedBlocks = [...trackBlocks].sort(
    (a, b) => a.timestamp - b.timestamp,
  );

  const buildClusterTooltipEvents = (clusterBlocks) => {
    const tooltipEventMap = new Map();
    clusterBlocks.forEach((block) => {
      (block.tooltipEvents || []).forEach((event) => {
        const key = `${event.eventId || 'event'}-${event.timestamp}`;
        tooltipEventMap.set(key, event);
      });
      const blockKey = `${block.eventId || 'event'}-${block.timestamp}`;
      tooltipEventMap.set(blockKey, block);
    });
    return Array.from(tooltipEventMap.values())
      .sort((a, b) => a.timestamp - b.timestamp)
      .map((e, i) => ({
        ...e,
        tooltipKey: `${e.eventId || 'event'}-${e.timestamp}-${i}`,
      }));
  };

  const getRenderWindow = (block) => {
    const visualDuration = block.isInstant
      ? totalDuration * 0.02
      : block.durationSeconds
        ? block.durationSeconds * 1000
        : totalDuration * 0.015;
    if (block.isLast) {
      return {
        start: Math.max(0, totalDuration - visualDuration),
        end: totalDuration,
      };
    }
    return {
      start: block.visualStart,
      end: Math.min(totalDuration, block.visualStart + visualDuration),
    };
  };

  let currentCluster = [];
  let currentClusterEnd = -Infinity;

  const applyCluster = () => {
    if (!currentCluster.length) return;
    const tooltipEvents = buildClusterTooltipEvents(currentCluster);
    currentCluster.forEach((block) => {
      block.tooltipEvents = tooltipEvents;
    });
  };

  sortedBlocks.forEach((block) => {
    const renderWindow = getRenderWindow(block);
    if (!currentCluster.length) {
      currentCluster = [block];
      currentClusterEnd = renderWindow.end;
      return;
    }
    if (renderWindow.start <= currentClusterEnd) {
      currentCluster.push(block);
      currentClusterEnd = Math.max(currentClusterEnd, renderWindow.end);
      return;
    }
    applyCluster();
    currentCluster = [block];
    currentClusterEnd = renderWindow.end;
  });

  applyCluster();
  return trackBlocks;
};

// ---------------------------------------------------------------------------
// 获取事件类型中文名
// ---------------------------------------------------------------------------
const getEventCodeName = (eventCode) => EVENT_TYPES[eventCode] || eventCode;

// ---------------------------------------------------------------------------
// 聚合 Tooltip 组件(支持细粒度事件类型)
// ---------------------------------------------------------------------------

const AggregatedTooltip = ({ events, children }) => {
  if (!events || events.length === 0) return children;

  const content = (
    <div className="gantt-tooltip-content">
      {events.length > 1 && (
        <div className="tooltip-header">
          <span className="tooltip-count">{events.length} 个事件</span>
        </div>
      )}
      {events.map((event, idx) => {
        const isLocation = event._type === 'location';
        const eventColor = event.isInstant
          ? EVENT_COLORS[event.eventCode] || '#999'
          : event.color || EVENT_COLORS[event.eventCode] || '#1890ff';

        return (
          <div key={event.tooltipKey || idx} className="tooltip-event-item">
            {isLocation ? (
              <>
                <div className="tooltip-event-type" style={{ color: '#52c41a' }}>
                  {event.locationType}定位
                </div>
                <div className="tooltip-event-name">{event.locationName}</div>
                <div className="tooltip-event-time">
                  {moment(event.timestamp).format('HH:mm')}
                </div>
                {event.isStoreLocation && (
                  <div className="tooltip-event-detail" style={{ color: '#52c41a' }}>
                    门店位置 ✓
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="tooltip-event-type" style={{ color: eventColor }}>
                  {getEventCodeName(event.eventCode)}
                </div>
                {event.storeName && (
                  <div className="tooltip-event-name">{event.storeName}</div>
                )}
                {event.courseName && (
                  <div className="tooltip-event-name">{event.courseName}</div>
                )}
                <div className="tooltip-event-time">
                  {moment(event.timestamp).format('HH:mm')}
                  {event.durationSeconds > 0 && (
                    <>
                      {' - '}
                      {moment(
                        event.timestamp + event.durationSeconds * 1000,
                      ).format('HH:mm')}
                    </>
                  )}
                </div>
                {event.durationSeconds > 0 && (
                  <div className="tooltip-event-detail">
                    {formatDuration(event.durationSeconds)}
                    {event.meetStandard !== undefined &&
                      (event.meetStandard ? ' | ✓ 达标' : ' | ✗ 未达标')}
                  </div>
                )}
              </>
            )}
            {idx < events.length - 1 && <div className="tooltip-divider" />}
          </div>
        );
      })}
    </div>
  );

  return (
    <Tooltip
      placement="top"
      title={content}
      overlayClassName="gantt-tooltip-overlay"
    >
      {children}
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// 主组件
// ---------------------------------------------------------------------------

const DailyActivityDialogDemo = ({ visible, onClose, dayData }) => {
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(true);
  const timelineHeaderRef = useRef(null);

  // 当天概况数据
  const summary = useMemo(() => {
    if (!dayData) return null;

    const events = dayData.events || [];
    const locations = dayData.locations || [];

    // 统计各事件类型次数
    const codeCounts = {};
    events.forEach((e) => {
      if (e.eventCode === 'ENTER_STORE' || e.eventCode === 'CHECKOUT') return;
      const code = e.eventCode || e.eventType;
      codeCounts[code] = (codeCounts[code] || 0) + 1;
    });

    const totalDuration = events.reduce(
      (sum, e) => sum + (e.durationSeconds || 0),
      0,
    );

    let minTime = null;
    let maxTime = null;
    events.forEach((e) => {
      if (!minTime || e.timestamp < minTime) minTime = e.timestamp;
      if (!maxTime || e.timestamp > maxTime) maxTime = e.timestamp;
    });
    locations.forEach((l) => {
      if (!minTime || l.timestamp < minTime) minTime = l.timestamp;
      if (!maxTime || l.timestamp > maxTime) maxTime = l.timestamp;
    });

    return {
      date: dayData.date,
      weekdayName: dayData.weekdayName,
      inStoreCount: dayData.inStoreEventTimes || 0,
      onlineCount: dayData.onlineTrainingTimes || 0,
      totalAvailableTimes: dayData.totalAvailableTimes || 0,
      meetStandard: dayData.meetStandard,
      totalDuration,
      minTime,
      maxTime,
      eventCount: events.length,
      locationCount: locations.length,
    };
  }, [dayData]);

  // 甘特图数据 - 细粒度事件类型版
  // 每次到店访问(进店→训练→离店)为一行轨道
  const ganttTracks = useMemo(() => {
    if (!dayData?.events?.length && !dayData?.locations?.length) return [];

    const events = dayData.events || [];
    const locations = dayData.locations || [];

    let minTime = null;
    let maxTime = null;

    events.forEach((e) => {
      if (!minTime || e.timestamp < minTime) minTime = e.timestamp;
      const endTime = e.timestamp + (e.durationSeconds || 0) * 1000;
      if (!maxTime || endTime > maxTime) maxTime = endTime;
    });
    locations.forEach((l) => {
      if (!minTime || l.timestamp < minTime) minTime = l.timestamp;
      if (!maxTime || l.timestamp > maxTime) maxTime = l.timestamp;
    });

    if (!minTime || !maxTime) return [];

    // 扩展到前后 1 小时
    minTime -= 60 * 60 * 1000;
    maxTime += 60 * 60 * 1000;
    const totalDuration = maxTime - minTime || 1;

    const tracks = [];

    // ── 按门店访问分组:连续的同门店事件视为一次访问 ──
    const storeEvents = events
      .filter((e) => e.eventType === 'inStore')
      .sort((a, b) => a.timestamp - b.timestamp);

    // 将连续同门店的事件聚合成一次访问
    const storeVisits = [];
    let currentVisit = null;
    storeEvents.forEach((e) => {
      if (
        currentVisit &&
        currentVisit.storeName === e.storeName &&
        e.timestamp - currentVisit.endTime < 30 * 60 * 1000 // 30分钟内视为同一次访问
      ) {
        currentVisit.events.push(e);
        currentVisit.endTime = Math.max(
          currentVisit.endTime,
          e.timestamp + (e.durationSeconds || 0) * 1000,
        );
      } else {
        if (currentVisit) storeVisits.push(currentVisit);
        currentVisit = {
          storeName: e.storeName,
          storeId: e.storeId || e.storeName,
          events: [e],
          endTime: e.timestamp + (e.durationSeconds || 0) * 1000,
        };
      }
    });
    if (currentVisit) storeVisits.push(currentVisit);

    storeVisits.forEach((visit, visitIdx) => {
      const trackId = `store-${visit.storeId || visitIdx}`;
      const blocks = [];

      visit.events.forEach((se) => {
        const startOffset = Math.max(0, se.timestamp - minTime);
        const isInstant = se.isInstant || se.durationSeconds === 0;
        const isLast = startOffset >= totalDuration - 1000;

        if (isInstant) {
          // 瞬时事件:菱形标记
          blocks.push({
            ...se,
            storeName: visit.storeName,
            left: (startOffset / totalDuration) * 100,
            width: 0,
            isInstant: true,
            isLast: false,
            color: EVENT_COLORS[se.eventCode] || '#999',
            visualStart: startOffset,
            visualEnd: startOffset,
          });
        } else {
          // 持续时间事件:条形
          const duration = se.durationSeconds * 1000;
          let leftPct = (startOffset / totalDuration) * 100;
          let widthPct = (duration / totalDuration) * 100;

          if (isLast) {
            leftPct = 100;
            widthPct = 0;
          } else if (leftPct + widthPct > 100) {
            widthPct = 100 - leftPct;
          }

          blocks.push({
            ...se,
            storeName: visit.storeName,
            left: leftPct,
            width: Math.max(widthPct, 1),
            isInstant: false,
            isLast,
            color: EVENT_COLORS[se.eventCode] || '#1890ff',
            visualStart: startOffset,
            visualEnd: startOffset + duration,
          });
        }
      });

      tracks.push({
        id: trackId,
        name: visit.storeName || '门店',
        type: 'inStore',
        color: '#1890ff',
        blocks: assignTrackTooltipEvents(blocks, totalDuration),
      });
    });

    // ── 入镜训练事件 ──
    const onlineEvents = events.filter((e) => e.eventType === 'onlineTraining');
    onlineEvents.forEach((event, idx) => {
      const key = event.courseId || event.courseName || 'unknown';
      const startOffset = Math.max(0, event.timestamp - minTime);
      const duration = (event.durationSeconds || 0) * 1000;
      const isLast = startOffset >= totalDuration - 1000;
      let leftPct = (startOffset / totalDuration) * 100;
      let widthPct = (duration / totalDuration) * 100;

      if (isLast) {
        leftPct = 100;
        widthPct = 0;
      } else if (leftPct + widthPct > 100) {
        widthPct = 100 - leftPct;
      }

      const block = {
        ...event,
        left: leftPct,
        width: Math.max(widthPct, 1),
        isInstant: false,
        isLast,
        color: EVENT_COLORS.ONLINE_TRAINING || '#fa8c16',
        visualStart: startOffset,
        visualEnd: startOffset + duration,
      };

      tracks.push({
        id: `online-${key}-${idx}`,
        name: event.courseName || '入镜训练',
        type: 'onlineTraining',
        color: EVENT_COLORS.ONLINE_TRAINING || '#fa8c16',
        blocks: assignTrackTooltipEvents([block], totalDuration),
      });
    });

    // ── 位置更新轨道 ──
    if (locations.length > 0) {
      const locationPoints = locations.map((loc) => {
        const startOffset = Math.max(0, loc.timestamp - minTime);
        return {
          ...loc,
          left: (startOffset / totalDuration) * 100,
          color: '#52c41a',
        };
      });
      tracks.push({
        id: 'location',
        name: '位置更新',
        type: 'location',
        color: '#52c41a',
        points: locationPoints,
      });
    }

    return tracks;
  }, [dayData]);

  // 甘特图时间范围
  const ganttTimeRange = useMemo(() => {
    const tracks = ganttTracks;
    if (!tracks.length) return { start: '', end: '' };

    const allItems = [];
    tracks.forEach((t) => {
      if (t.blocks) allItems.push(...t.blocks);
      if (t.points) allItems.push(...t.points);
    });
    if (!allItems.length) return { start: '', end: '' };

    let minT = null;
    let maxT = null;
    allItems.forEach((item) => {
      const ts = item.timestamp;
      if (!minT || ts < minT) minT = ts;
      if (!maxT || ts > maxT) maxT = ts;
      if (item.durationSeconds) {
        const end = ts + item.durationSeconds * 1000;
        if (end > maxT) maxT = end;
      }
    });

    minT -= 60 * 60 * 1000;
    maxT += 60 * 60 * 1000;

    return {
      start: moment(minT).format('HH:mm'),
      end: moment(maxT).format('HH:mm'),
    };
  }, [ganttTracks]);

  // 甘特图时间刻度(每 2 小时一个刻度)
  const ganttTicks = useMemo(() => {
    if (!ganttTracks.length) return [];

    const allItems = [];
    ganttTracks.forEach((t) => {
      if (t.blocks) allItems.push(...t.blocks);
      if (t.points) allItems.push(...t.points);
    });
    if (!allItems.length) return [];

    let minT = null;
    let maxT = null;
    allItems.forEach((item) => {
      const ts = item.timestamp;
      if (!minT || ts < minT) minT = ts;
      if (!maxT || ts > maxT) maxT = ts;
      if (item.durationSeconds) {
        const end = ts + item.durationSeconds * 1000;
        if (end > maxT) maxT = end;
      }
    });

    minT -= 60 * 60 * 1000;
    maxT += 60 * 60 * 1000;
    const totalDuration = maxT - minT;

    const ticks = [];
    const intervalMs = 2 * 60 * 60 * 1000;
    let currentTime = Math.ceil(minT / intervalMs) * intervalMs;

    while (currentTime < maxT) {
      const offset = currentTime - minT;
      const pct = (offset / totalDuration) * 100;
      ticks.push({
        time: moment(currentTime).format('HH:mm'),
        position: pct,
      });
      currentTime += intervalMs;
    }

    return ticks;
  }, [ganttTracks]);

  // 图例数据 - 当日出现的事件类型
  const activeEventCodes = useMemo(() => {
    if (!dayData?.events?.length) return [];
    const codes = new Set();
    dayData.events.forEach((e) => {
      if (e.subEvents) {
        e.subEvents.forEach((se) => codes.add(se.eventCode));
      } else {
        codes.add(e.eventCode || e.eventType);
      }
    });
    return Array.from(codes);
  }, [dayData]);

  // 合并所有事件按时间排序
  const allEvents = useMemo(() => {
    if (!dayData) return [];
    const events = [];

    (dayData.events || []).forEach((e) => {
      if (e.eventType === 'inStore' && e.subEvents) {
        // 展开子事件到时间轴
        e.subEvents.forEach((se) => {
          events.push({
            ...se,
            _type: 'event',
            storeName: e.storeName,
            storeAddress: e.storeAddress,
            parentEventType: 'inStore',
          });
        });
      } else {
        events.push({ ...e, _type: 'event' });
      }
    });

    const locations = (dayData.locations || []).map((l) => ({
      ...l,
      _type: 'location',
    }));

    return [...events, ...locations].sort((a, b) => a.timestamp - b.timestamp);
  }, [dayData]);

  useEffect(() => {
    if (visible) {
      setIsTimelineExpanded(true);
    }
  }, [visible]);

  const toggleTimeline = () => {
    setIsTimelineExpanded((prev) => {
      const next = !prev;
      if (next) {
        setTimeout(() => {
          timelineHeaderRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      }
      return next;
    });
  };

  // 当天概况
  const renderSummary = () => {
    if (!summary) return null;

    return (
      <div className="summary-section">
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="到店训练"
              value={summary.inStoreCount}
              suffix="次"
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="入镜训练"
              value={summary.onlineCount}
              suffix="次"
              valueStyle={{ color: '#fa8c16' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="有效次数"
              value={summary.totalAvailableTimes}
              suffix="次"
            />
          </Col>
          <Col span={6}>
            <div className="summary-status">
              <div className="status-label">达标状态</div>
              <div className="status-value">
                {summary.meetStandard ? (
                  <Tag color="success" icon={<CheckCircleOutlined />}>
                    已达标
                  </Tag>
                ) : summary.totalAvailableTimes === 0 ? (
                  <Tag>无记录</Tag>
                ) : (
                  <Tag color="error" icon={<CloseCircleOutlined />}>
                    未达标
                  </Tag>
                )}
              </div>
            </div>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={8}>
            <div className="summary-info">
              <ClockCircleOutlined style={{ marginRight: 8 }} />
              活动时间:
              {summary.minTime && summary.maxTime
                ? `${moment(summary.minTime).format('HH:mm')} - ${moment(
                    summary.maxTime,
                  ).format('HH:mm')}`
                : '-'}
            </div>
          </Col>
          <Col span={8}>
            <div className="summary-info">
              <ClockCircleOutlined style={{ marginRight: 8 }} />
              总时长:{formatDuration(summary.totalDuration)}
            </div>
          </Col>
          <Col span={8}>
            <div className="summary-info">
              <EnvironmentOutlined style={{ marginRight: 8 }} />
              位置更新:{summary.locationCount}次
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  // 甘特图图例
  const renderGanttLegend = () => {
    if (!activeEventCodes.length) return null;

    return (
      <div className="gantt-legend">
        {activeEventCodes.map((code) => (
          <span key={code} className="legend-item">
            <span
              className="legend-dot"
              style={{ backgroundColor: EVENT_COLORS[code] || '#999' }}
            />
            {getEventCodeName(code)}
          </span>
        ))}
        <span className="legend-item">
          <span className="legend-diamond" />
          瞬时事件
        </span>
      </div>
    );
  };

  // 甘特图
  const renderGanttChart = () => {
    if (!ganttTracks.length) return null;

    return (
      <div className="gantt-section">
        <div className="section-subtitle">活动时间分布</div>
        {renderGanttLegend()}
        <div className="gantt-chart">
          {/* 时间刻度头 */}
          <div className="gantt-header">
            <div className="gantt-track-name-header" />
            <div className="gantt-timeline-area">
              <div className="gantt-time-labels">
                <span className="time-label">{ganttTimeRange.start}</span>
                <span className="time-label">{ganttTimeRange.end}</span>
              </div>
              {/* 时间刻度线 */}
              <div className="gantt-ticks">
                {ganttTicks.map((tick, idx) => (
                  <div
                    key={idx}
                    className="gantt-tick"
                    style={{ left: `${tick.position}%` }}
                  >
                    <span className="tick-label">{tick.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 轨道区域 */}
          <div className="gantt-body">
            {ganttTracks.map((track) => (
              <div key={track.id} className="gantt-track">
                {/* 轨道名称 */}
                <div className="gantt-track-name">
                  <span
                    className="track-type-indicator"
                    style={{ backgroundColor: track.color }}
                  />
                  <span className="track-name-text">{track.name}</span>
                </div>

                {/* 轨道条 */}
                <div className="gantt-track-bars">
                  {/* 背景刻度线 */}
                  {ganttTicks.map((tick, idx) => (
                    <div
                      key={idx}
                      className="track-grid-line"
                      style={{ left: `${tick.position}%` }}
                    />
                  ))}

                  {/* 事件条(持续时间事件) */}
                  {track.blocks?.map((block, idx) => {
                    const hasTooltipEvents =
                      block.tooltipEvents && block.tooltipEvents.length > 0;
                    const tooltipEvents = hasTooltipEvents
                      ? block.tooltipEvents
                      : [block];

                    if (block.isInstant) {
                      // 瞬时事件:菱形标记
                      return (
                        <AggregatedTooltip
                          key={`instant-${idx}`}
                          events={tooltipEvents}
                        >
                          <div
                            className="track-instant-marker"
                            style={{
                              left: `${block.left}%`,
                              backgroundColor: block.color,
                            }}
                          />
                        </AggregatedTooltip>
                      );
                    }

                    return (
                      <AggregatedTooltip
                        key={`bar-${idx}`}
                        events={tooltipEvents}
                      >
                        <div
                          className={`track-block ${block.isLast ? 'track-block-last' : ''}`}
                          style={{
                            left: `${block.left}%`,
                            width: `${block.width}%`,
                            backgroundColor: block.color,
                          }}
                        >
                          {/* bar 内显示细粒度事件类型名称 */}
                          {!block.isLast && block.width > 4 && (
                            <span className="track-block-label">
                              {getEventCodeName(block.eventCode)}
                            </span>
                          )}
                        </div>
                      </AggregatedTooltip>
                    );
                  })}

                  {/* 位置点 */}
                  {track.points?.map((point, idx) => (
                    <AggregatedTooltip key={idx} events={[point]}>
                      <div
                        className="track-point"
                        style={{
                          left: `${point.left}%`,
                          backgroundColor: point.color,
                        }}
                      >
                        {point.isStoreLocation && (
                          <span className="point-badge">✓</span>
                        )}
                      </div>
                    </AggregatedTooltip>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // 时间轴
  const renderTimeline = () => {
    if (!allEvents.length) {
      return <Empty description="当天没有活动记录" />;
    }

    return (
      <div className="timeline-section">
        <div
          className="timeline-collapse-header"
          ref={timelineHeaderRef}
          onClick={toggleTimeline}
        >
          <div className="section-subtitle" style={{ marginBottom: 0 }}>
            活动明细记录
          </div>
          <div className="toggle-btn">
            <span>{isTimelineExpanded ? '收起明细' : '查看明细'}</span>
          </div>
        </div>

        {isTimelineExpanded && (
          <div className="timeline-collapse-content">
            <div className="custom-timeline">
              {allEvents.map((event, index) => {
                const isLocation = event._type === 'location';
                const eventColor =
                  EVENT_COLORS[event.eventCode] || '#1890ff';
                const eventName = getEventCodeName(event.eventCode);
                const isInstant =
                  event.isInstant || event.durationSeconds === 0;

                return (
                  <div key={index} className="timeline-item">
                    <div className="timeline-item-tail">
                      <div
                        className="timeline-item-dot"
                        style={{
                          backgroundColor: isLocation
                            ? '#52c41a'
                            : eventColor,
                        }}
                      />
                    </div>
                    <div className="timeline-item-body">
                      <div className="timeline-item-time">
                        {moment(event.timestamp).format('HH:mm')}
                      </div>
                      <div className="timeline-event-content">
                        {isLocation ? (
                          <>
                            <div className="event-header">
                              <Tag color="green">
                                <EnvironmentOutlined /> 位置更新
                              </Tag>
                              <span className="event-name">
                                {event.locationName}
                              </span>
                            </div>
                            <div className="event-details">
                              <div className="detail-row">
                                <span className="detail-label">
                                  定位方式:
                                </span>
                                <span className="detail-value">
                                  {event.locationType}
                                </span>
                              </div>
                              {event.isStoreLocation && (
                                <div className="detail-row">
                                  <span className="detail-label">
                                    门店位置:
                                  </span>
                                  <span className="detail-value">
                                    <Tag color="success">✓</Tag>
                                  </span>
                                </div>
                              )}
                              {event.accuracy && (
                                <div className="detail-row">
                                  <span className="detail-label">
                                    精度:
                                  </span>
                                  <span className="detail-value">
                                    {event.accuracy}m
                                  </span>
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="event-header">
                              <Tag
                                color={
                                  EVENT_COLORS[event.eventCode] || '#1890ff'
                                }
                              >
                                {eventName}
                              </Tag>
                              {event.storeName && (
                                <span className="event-name">
                                  {event.storeName}
                                </span>
                              )}
                              {event.courseName && (
                                <span className="event-name">
                                  {event.courseName}
                                </span>
                              )}
                            </div>
                            <div className="event-details">
                              {!isInstant && (
                                <div className="detail-row">
                                  <span className="detail-label">
                                    持续时间:
                                  </span>
                                  <span className="detail-value">
                                    {formatDuration(event.durationSeconds)}
                                  </span>
                                </div>
                              )}
                              {event.meetStandard !== undefined && (
                                <div className="detail-row">
                                  <span className="detail-label">
                                    达标状态:
                                  </span>
                                  <span className="detail-value">
                                    {event.meetStandard ? (
                                      <Tag color="success">✓ 达标</Tag>
                                    ) : (
                                      <Tag color="error">✗ 未达标</Tag>
                                    )}
                                  </span>
                                </div>
                              )}
                              {event.reviewStatus && (
                                <div className="detail-row">
                                  <span className="detail-label">
                                    审核状态:
                                  </span>
                                  <span className="detail-value">
                                    {event.reviewStatus === 'PASS' ? (
                                      <Tag color="success">审核通过</Tag>
                                    ) : event.reviewStatus === 'PENDING' ? (
                                      <Tag color="warning">待审核</Tag>
                                    ) : (
                                      <Tag color="error">审核不通过</Tag>
                                    )}
                                  </span>
                                </div>
                              )}
                              {event.locationVerified && (
                                <div className="detail-row">
                                  <span className="detail-label">
                                    位置验证:
                                  </span>
                                  <span className="detail-value">
                                    <Tag color="success">
                                      <EnvironmentOutlined /> 位置已验证
                                    </Tag>
                                  </span>
                                </div>
                              )}
                              {event.caloriesBurnt !== undefined && (
                                <div className="detail-row">
                                  <span className="detail-label">
                                    消耗热量:
                                  </span>
                                  <span className="detail-value">
                                    {event.caloriesBurnt} 千卡
                                  </span>
                                </div>
                              )}
                              {event.heartRate !== undefined && (
                                <div className="detail-row">
                                  <span className="detail-label">
                                    平均心率:
                                  </span>
                                  <span className="detail-value">
                                    {event.heartRate} 次/分
                                  </span>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={
        summary
          ? `${summary.date} ${summary.weekdayName} 活动详情`
          : '活动详情'
      }
      width={900}
      footer={null}
      destroyOnClose
      centered
      className="daily-activity-dialog"
    >
      <div className="daily-activity-content">
        {renderSummary()}
        {renderGanttChart()}
        {renderTimeline()}
      </div>
    </Modal>
  );
};

export default DailyActivityDialogDemo;
